// ===============================================
// File: src/contexts/KilnContext.jsx
// Persistence: Local-first via StorageContext (Folder or Drive)
// Stores kiln metadata for capacity planning and AI packing
// ===============================================

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useStorage } from "./StorageContext";
import { useNotifications } from "./NotificationsContext";
import {
  estimateKilnLoad,
  estimateUsableSpaceUnitsFromKiln,
} from "../ai/estimators/kilnCapacityEstimator";

const FILE_PATH = "studio/kilns/kilns.json";

export const KilnContext = createContext(null);

function toast(message, type = "info") {
  try {
    window.dispatchEvent(new CustomEvent("mudo:toast", { detail: { type, message } }));
  } catch {}
}

function safeId() {
  try {
    return globalThis.crypto?.randomUUID?.() ?? `kiln_${Date.now()}`;
  } catch {
    return `kiln_${Date.now()}`;
  }
}

function nowIso() {
  try {
    return new Date().toISOString();
  } catch {
    return String(Date.now());
  }
}

export function KilnProvider({ children }) {
  const {
    hasFolder,
    isDriveLinked,
    isDriveAuthed,
    loadJSON,
    saveJSON,
  } = useStorage();
  const { notifyAndInbox } = useNotifications() || {};

  const hasWorkspace = !!hasFolder || (!!isDriveLinked && !!isDriveAuthed);

  const [kilns, setKilns] = useState([]);
  const [selectedKilnId, setSelectedKilnId] = useState(null);
  const loadedRef = useRef(false);
  const saveDebounce = useRef(0);

  const notifyKiln = useCallback(
    ({ id, message, level = "info" }) => {
      toast(message, level);
      notifyAndInbox?.({
        id,
        createdAt: Date.now(),
        level,
        title: "Kilns",
        message,
        source: "kiln",
        world: "studio",
        entity: { type: "kiln" },
        action: { kind: "NAVIGATE", to: "/dashboard/kilns" },
      });
    },
    [notifyAndInbox]
  );

  // ----- hydrate on workspace availability -----
  useEffect(() => {
    let alive = true;
    loadedRef.current = false;

    (async () => {
      if (!hasWorkspace) {
        if (alive) {
          setKilns([]);
          setSelectedKilnId(null);
        }
        loadedRef.current = true;
        return;
      }
      try {
        const disk = await loadJSON(FILE_PATH, []);
        if (alive) setKilns(Array.isArray(disk) ? disk : []);
      } catch (e) {
        console.error("Failed to load kilns from Drive:", e);
        notifyKiln({ id: "kilns-load-failed", message: "Failed to load kilns", level: "error" });
        if (alive) setKilns([]);
      } finally {
        loadedRef.current = true;
      }
    })();

    return () => { alive = false; };
  }, [hasWorkspace, loadJSON, notifyKiln]);

  // ----- persist when kilns change (debounced) -----
  useEffect(() => {
    if (!loadedRef.current || !hasWorkspace) return;
    clearTimeout(saveDebounce.current);
    saveDebounce.current = setTimeout(async () => {
      try { 
        await saveJSON(FILE_PATH, kilns);
      } catch (err) { 
        console.error("Failed to save kilns to Drive:", err);
        notifyKiln({ id: "kilns-save-failed", message: "Failed to save kilns", level: "error" }); 
      }
    }, 500);
    return () => clearTimeout(saveDebounce.current);
  }, [kilns, hasWorkspace, saveJSON, notifyKiln]);

  // ----- rehydrate after restore / disconnect -----
  useEffect(() => {
    const onRestored = async () => {
      if (!hasWorkspace) return;
      try {
        const disk = await loadJSON(FILE_PATH, []);
        setKilns(Array.isArray(disk) ? disk : []);
      } catch (err) {
        console.error("Failed to rehydrate kilns:", err);
        notifyKiln({
          id: "kilns-rehydrate-failed",
          message: "Failed to rehydrate kilns",
          level: "error",
        });
      }
    };

    const onDisconnect = () => {
      setKilns([]);
      setSelectedKilnId(null);
      loadedRef.current = false;
    };

    window.addEventListener("mudo:vault-restored", onRestored);
    window.addEventListener("mudo:files-restored", onRestored);
    window.addEventListener("mudo:workspace-disconnected", onDisconnect);
    return () => {
      window.removeEventListener("mudo:vault-restored", onRestored);
      window.removeEventListener("mudo:files-restored", onRestored);
      window.removeEventListener("mudo:workspace-disconnected", onDisconnect);
    };
  }, [hasWorkspace, loadJSON, notifyKiln]);

  const selectedKiln = useMemo(
    () => (kilns || []).find((k) => String(k.id) === String(selectedKilnId)) || null,
    [kilns, selectedKilnId]
  );

  /* ---------- CRUD ---------- */
  const addKiln = useCallback((partial) => {
    const now = nowIso();
    const base = {
      id: partial?.id || safeId(),
      name: partial?.name || "New Kiln",
      type: partial?.type || "Unknown",
      capacityLitres: partial?.capacityLitres ?? null,
      internalWidthCm: partial?.internalWidthCm ?? null,
      internalDepthCm: partial?.internalDepthCm ?? null,
      internalHeightCm: partial?.internalHeightCm ?? null,
      shelfCount: partial?.shelfCount ?? null,
      shelfDiameters: Array.isArray(partial?.shelfDiameters) ? partial.shelfDiameters : [],
      maxCone: partial?.maxCone ?? null,
      usableSpaceUnits: partial?.usableSpaceUnits ?? null,
      notes: partial?.notes || "",
      createdAt: partial?.createdAt || now,
      updatedAt: now,
    };

    const withEstimate = {
      ...base,
      usableSpaceUnits:
        base.usableSpaceUnits ?? estimateUsableSpaceUnitsFromKiln(base),
    };

    setKilns((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return [withEstimate, ...prev];
    });

    return base.id;
  }, []);

  const updateKiln = useCallback((idOrObj, maybePatch) => {
    let id = idOrObj;
    let patch = maybePatch;
    if (typeof idOrObj === "object" && idOrObj !== null && maybePatch === undefined) {
      id = idOrObj.id;
      patch = idOrObj;
    }
    if (!id) return;

    setKilns((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return prev.map((k) => {
        if (String(k.id) !== String(id)) return k;
        const mixed = { ...k, ...patch };
        const resolvedUsable =
          patch?.usableSpaceUnits !== undefined
            ? patch.usableSpaceUnits
            : k.usableSpaceUnits ?? estimateUsableSpaceUnitsFromKiln(mixed);

        return {
          ...mixed,
          usableSpaceUnits: resolvedUsable,
          updatedAt: nowIso(),
        };
      });
    });
  }, []);

  const deleteKiln = useCallback((id) => {
    setKilns((prevRaw) => {
      const prev = Array.isArray(prevRaw) ? prevRaw : [];
      return prev.filter((k) => String(k.id) !== String(id));
    });
    setSelectedKilnId((prev) => (String(prev) === String(id) ? null : prev));
  }, []);

  const getKilnCapacityInfo = useCallback((kiln, loadItems) => {
    return estimateKilnLoad(kiln, loadItems);
  }, []);

  const value = useMemo(
    () => ({
      kilns,
      selectedKiln,
      selectedKilnId,
      setSelectedKilnId,
      addKiln,
      updateKiln,
      deleteKiln,
      getKilnCapacityInfo,
    }),
    [kilns, selectedKiln, selectedKilnId, addKiln, updateKiln, deleteKiln, getKilnCapacityInfo]
  );

  return <KilnContext.Provider value={value}>{children}</KilnContext.Provider>;
}

export function useKilns() {
  const ctx = useContext(KilnContext);
  if (!ctx) throw new Error("useKilns must be used within KilnProvider");
  return ctx;
}
